import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useNetworkPricesStore } from '../NetworkPrices.store';

type FocusedPriceInput = { label: string | null; selectionStart: number | null; selectionEnd: number | null };
type NetworkPricesTableSize = { containerRef: RefObject<HTMLDivElement>; width: number | undefined; height: number | undefined };

// The container fills the space the page leaves for the grid; the Table takes its size so rows scroll inside it
// and the horizontal scrollbar stays at the bottom of the viewport.
export const useNetworkPricesTableSize = (): NetworkPricesTableSize => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measuredWidth = useRef<number>();
  const focusedInput = useRef<FocusedPriceInput | null>(null);
  const [width, setWidth] = useState<number>();
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measureWidth = (): void => {
      const nextWidth = container.clientWidth;
      if (nextWidth <= 0 || nextWidth === measuredWidth.current) return;

      // The Table remounts on a new width: keep the active edit as a draft (no save) and refocus it afterwards.
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLInputElement && container.contains(activeElement)) {
        const { selectionStart, selectionEnd } = activeElement;
        focusedInput.current = { label: activeElement.getAttribute('aria-label'), selectionStart, selectionEnd };
        useNetworkPricesStore.getState().startTableRemount();
      }
      measuredWidth.current = nextWidth;
      setWidth(nextWidth);
    };
    const measureSize = (): void => {
      measureWidth();
      // A new height only resizes the Table (no remount), so an active edit is left alone.
      const nextHeight = container.clientHeight;
      if (nextHeight > 0) setHeight(nextHeight);
    };
    measureSize();
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measureSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const focused = focusedInput.current;
    if (!focused) return;
    focusedInput.current = null;

    const input = Array.from(containerRef.current?.querySelectorAll('input') ?? []).find(item => item.getAttribute('aria-label') === focused.label);
    input?.focus({ preventScroll: true });
    input?.setSelectionRange(focused.selectionStart, focused.selectionEnd);
    useNetworkPricesStore.getState().finishTableRemount();
  }, [width]);

  return { containerRef, width, height };
};
